import { useState, useMemo } from 'react';
import { useToast } from '@chakra-ui/react';
import { useNotifications } from './useNotification';
import * as notificationApi from '../Api/notificationApi';

export const useNotificationManager = (companyId, employeeId) => {
	const [filter, setFilter] = useState('unread');
	const [isDeletingAll, setIsDeletingAll] = useState(false);

	const toast = useToast();

	const {
		data: notifications,
		isLoading: loading,
		isError: error,
		refetch: refetchNotifications,
	} = useNotifications(companyId);

	// Filter notifications for the current user and count unread ones
	const userNotifications = useMemo(() => {
		return Array.isArray(notifications)
			? notifications.filter((notification) => {
					if (
						!notification.recipients ||
						!Array.isArray(notification.recipients)
					) {
						return false;
					}
					const userRecipient = notification.recipients.find(
						(recipient) => recipient.employeeId === employeeId,
					);
					return userRecipient && !userRecipient.isRead; // Only unread notifications
				})
			: [];
	}, [notifications, employeeId]);

	// Get all notifications for the current user (read and unread)
	const allUserNotifications = useMemo(() => {
		return Array.isArray(notifications)
			? notifications.filter((notification) => {
					if (
						!notification.recipients ||
						!Array.isArray(notification.recipients)
					) {
						return false;
					}
					return notification.recipients.some(
						(recipient) => recipient.employeeId === employeeId,
					);
				})
			: [];
	}, [notifications, employeeId]);

	// Filter notifications based on current filter
	const filteredNotifications = useMemo(() => {
		return allUserNotifications.filter((notification) => {
			const userRecipient = notification.recipients.find(
				(recipient) => recipient.employeeId === employeeId,
			);
			if (!userRecipient) return false;

			switch (filter) {
				case 'unread':
					return !userRecipient.isRead;
				case 'read':
					return userRecipient.isRead;
				default:
					return true;
			}
		});
	}, [allUserNotifications, filter, employeeId]);

	// Handle notification actions
	const handleMarkAsRead = async (notificationId) => {
		try {
			await notificationApi.markNotificationAsRead(notificationId, employeeId);
			refetchNotifications();
			toast({
				title: 'Notification marked as read',
				status: 'success',
				duration: 2000,
				isClosable: true,
			});
		} catch (error) {
			toast({
				title: 'Error marking notification as read',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
		}
	};

	const handleMarkAsUnread = async (notificationId) => {
		try {
			await notificationApi.markNotificationAsUnread(notificationId, employeeId);
			refetchNotifications();
			toast({
				title: 'Notification marked as unread',
				status: 'success',
				duration: 2000,
				isClosable: true,
			});
		} catch (error) {
			toast({
				title: 'Error marking notification as unread',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
		}
	};

	const handleDeleteNotification = async (notificationId) => {
		try {
			await notificationApi.deleteNotification(notificationId, employeeId);
			refetchNotifications();
			toast({
				title: 'Notification deleted',
				status: 'success',
				duration: 2000,
				isClosable: true,
			});
		} catch (error) {
			toast({
				title: 'Error deleting notification',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
		}
	};

	const handleMarkAllAsRead = async () => {
		try {
			await notificationApi.markAllNotificationsAsRead(companyId, employeeId);
			refetchNotifications();
			toast({
				title: 'All notifications marked as read',
				status: 'success',
				duration: 2000,
				isClosable: true,
			});
		} catch (error) {
			toast({
				title: 'Error marking all notifications as read',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
		}
	};

	const handleDeleteAll = async () => {
		if (isDeletingAll || allUserNotifications.length === 0) return;

		setIsDeletingAll(true);
		try {
			await notificationApi.deleteAllNotifications(companyId, employeeId);
			refetchNotifications();
			toast({
				title: 'All notifications deleted',
				status: 'success',
				duration: 2000,
				isClosable: true,
			});
		} catch (error) {
			toast({
				title: 'Error deleting notifications',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
		} finally {
			setIsDeletingAll(false);
		}
	};

	return {
		// Data
		userNotifications,
		allUserNotifications,
		filteredNotifications,
		loading,
		error,

		// State
		filter,
		isDeletingAll,

		// Setters
		setFilter,

		// Handlers
		handleMarkAsRead,
		handleMarkAsUnread,
		handleDeleteNotification,
		handleMarkAllAsRead,
		handleDeleteAll,
	};
};
